import { useState } from 'react'
import { Checkbox } from '../../../components/ui/Checkbox'
import {
  facetValueCount,
  matchesFacetSearchText,
  seriesExactnessDisclosure,
} from '../catalog-series-facet-filter'
import type { CatalogFacet } from '../types'

export interface CatalogSeriesFacetSidebarProps {
  /** Already narrowed to the facets this page may render a control for at
   * the CURRENT selection (see `useCatalogFacets`/`isSeriesFacetRenderable`,
   * plan §1/§3) — this component renders whatever it is given, with no
   * gating re-derivation of its own. */
  readonly facets: readonly CatalogFacet[]
  /** One value per facet name (the BFF's `specs[<name>]=<value>` only
   * accepts a single value per name — a repeated name 400s), keyed by
   * `CatalogFacet.name`. */
  readonly selection: Readonly<Record<string, string>>
  readonly onChange: (selection: Readonly<Record<string, string>>) => void
  /**
   * UID-387 — true while a selection-change re-fetch (the live drilldown) is
   * in flight, i.e. `useCatalogFacets`'s `status === 'loading'` on a
   * NON-initial fetch. `facets` in that case is still the PREVIOUS answer
   * (the hook deliberately keeps it rather than blanking to `[]`), so this
   * renders an inline, non-blocking "Updating filters…" notice rather than
   * replacing the sidebar with a skeleton — the live drilldown has been
   * measured up to ~5.5s for a broad selection, and silently leaving stale
   * checkboxes on screen with no affordance would read as unresponsive.
   */
  readonly isPending?: boolean
}

/**
 * The left-hand facet sidebar on `CatalogSeriesPage` (ADR-0120/ADR-0121,
 * UID-387 existential live-count model) — "Clear all", a client-side
 * "Search filters" label search, and one checkbox group per queried facet.
 * A NEW component: this page's facet data is a queried per-category
 * catalogue (`{name, values, grains, valueCounts?}`), not the per-series
 * dimension `range`/`enum` segments
 * `CatalogSeriesFilterSidebar`/`catalog-series-filters.ts` derive from a
 * series' already-fetched rows, so extending that component would mean
 * branching most of its body on which shape it got — reusing its tested CSS
 * classes and `Checkbox` primitive instead (ADR-0120 D9 asks for reuse
 * generally; this is the point where the two data shapes diverge enough that
 * a shared component would be the incoherence D9 itself warns against).
 *
 * Deliberately checkbox-per-value rather than a radio group, to match the
 * mock's visual language, but a facet's group behaves single-select under
 * the hood (checking a value un-checks any other value already selected for
 * that SAME facet) — the BFF's `specs[<name>]=<value>` accepts only one
 * value per facet name today (a repeated name is a 400, "AND vs OR is not
 * decided at the parser" per UID-384's own tests), so offering a multi-check
 * group would let a shopper build a selection the backend cannot express.
 * Multi-select-per-facet (OR within one facet) is a real, separate scope
 * decision the plan explicitly defers (§3) — not built here.
 *
 * Live per-value counts (`itemCount`/`seriesCount` off `facet.valueCounts`)
 * render next to a value's label when the catalogue carries them — i.e. once
 * at least one OTHER facet is already selected (UID-387's live drilldown).
 * On the unscoped, zero-selection view `valueCounts` is omitted, and values
 * render with no count, exactly as before this change. The retained
 * invariance measurement surfaces as an optional secondary badge
 * ("exact for N of M") next to the facet's own legend when the wire still
 * marks it series-safe — a DIFFERENT claim from the live count (universal
 * vs. existential, plan §1) and deliberately not blended into one number.
 *
 * No collapsible/accordion chrome (the mock's affordance) — every section
 * renders always-open. There is no existing Collapsible/Accordion primitive
 * in this repo, and with at most a handful of facets per category collapsing
 * buys little and costs a first component + the extra keyboard/AT affordance
 * work correctly, right now. Revisit if a category ships enough facets that
 * always-open gets noisy.
 */
export function CatalogSeriesFacetSidebar({
  facets,
  selection,
  onChange,
  isPending = false,
}: CatalogSeriesFacetSidebarProps) {
  const [searchText, setSearchText] = useState('')
  const visibleFacets = facets.filter((facet) => matchesFacetSearchText(facet, searchText))

  function handleToggle(facetName: string, value: string, checked: boolean) {
    if (!checked) {
      const { [facetName]: _removed, ...rest } = selection
      onChange(rest)
      return
    }
    onChange({ ...selection, [facetName]: value })
  }

  return (
    <nav className="catalog-series-filter-sidebar" aria-label="Filter series">
      <div className="catalog-series-filter-header">
        <button type="button" className="catalog-series-filter-clear" onClick={() => onChange({})}>
          Clear all
        </button>
      </div>

      {/* `<output>` (implicit `status` role) + `aria-live="polite"` so a
       * screen reader announces the transition without stealing focus, and
       * an empty string when not pending so nothing is announced at all on
       * first render. */}
      <output className="catalog-series-facet-pending" aria-live="polite">
        {isPending ? 'Updating filters…' : ''}
      </output>

      <input
        type="search"
        className="catalog-series-facet-search"
        aria-label="Search filters"
        placeholder="Search filters"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
      />

      {visibleFacets.length === 0 && (
        <p className="catalog-series-facet-empty">No filters match “{searchText.trim()}”.</p>
      )}

      {visibleFacets.map((facet) => {
        const exactness = seriesExactnessDisclosure(facet)
        return (
          <fieldset key={facet.name} className="catalog-series-filter-section">
            <legend className="catalog-series-filter-legend">{facet.name}</legend>
            {/* Deliberately OUTSIDE `<legend>` — a fieldset's accessible name is
             * its legend's text content, and folding the disclosure in there
             * would rename the GROUP's name to "Brand · ✓ exact for 145 of
             * 145" for every assistive-tech/test query, not just visually
             * append a badge. A sibling element keeps the group's accessible
             * name exactly the facet name. */}
            {exactness && <p className="catalog-series-filter-exactness">✓ {exactness}</p>}
            {facet.values.map((value) => {
              const count = facetValueCount(facet, value)
              return (
                <Checkbox
                  key={value}
                  label={
                    count ? (
                      <>
                        {value}{' '}
                        <span className="catalog-series-filter-count">
                          ({count.seriesCount} series)
                        </span>
                      </>
                    ) : (
                      value
                    )
                  }
                  checked={selection[facet.name] === value}
                  onChange={(checked) => handleToggle(facet.name, value, checked)}
                />
              )
            })}
          </fieldset>
        )
      })}
    </nav>
  )
}
